import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import {
  EdgeDirection,
  EdgeFilterModeEnum,
  NetworkTraceModel,
  ObjectTypeInfoViewModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  QueryOutputMode,
} from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  AbcDropDownBoxComponent,
  AbcNumberBoxComponent,
  AbcPopupComponent,
  _AbcCheckBoxComponent,
} from '@barthauer/abc/ui-components';
import { Observable } from 'rxjs';
import { BasysMapFrameService } from '../basys-map-frame.service';
import { NetworkTracingInfo } from '../models/mapsData.model';

@Component({
    selector: 'barthauer-networktracing',
    templateUrl: './networktracing.component.html',
    styleUrls: ['./networktracing.component.scss'],
    standalone: false
})
export class NetworktracingComponent implements OnInit {
  @ViewChild('networkTracingPopup') networkTracingPopup: AbcPopupComponent;
  @ViewChild('dropDownPipe') dropDownPipe: AbcDropDownBoxComponent;
  @ViewChild('dropDownEdgeDirection', { static: true }) dropDownEdgeDirection: AbcDropDownBoxComponent;
  @ViewChild('maxEdgeNumberBox', { static: true }) maxEdgeNumberBox: AbcNumberBoxComponent;
  @ViewChild('stopSearchDropDown', { static: true }) stopSearchDropDown: AbcDropDownBoxComponent;

  @ViewChild('filterModeDropDown', { static: true }) filterModeDropDown: AbcDropDownBoxComponent;
  @ViewChild('filterValueDropDown', { static: true }) filterValueDropDown: AbcDropDownBoxComponent;
  @ViewChild('connectionPipeCheck', { static: true }) connectionPipeCheck: _AbcCheckBoxComponent;
  @ViewChild('onlyStartEndCheck', { static: true }) onlyStartEndCheck: _AbcCheckBoxComponent;

  @Output() onDialogClosed: EventEmitter<NetworkTracingInfo> = new EventEmitter();
  @Output() onDialogCancelled: EventEmitter<void> = new EventEmitter();

  public isPopupVisible: boolean = true;

  public edgeDirection: EdgeDirection = EdgeDirection.StartToEnd;
  public selectedPipeId: string = undefined;
  public maxEdgeCount: number | undefined = undefined;
  public searchStopOption: number = 0;
  public includeConnectionPipe: boolean = false;
  public onlyStartEndEdges: boolean = false;
  public edgeFilterMode: EdgeFilterModeEnum = EdgeFilterModeEnum.None;
  public edgeFilterValue: string = undefined;

  public res$: Observable<ResourceSet>;
  public res: ResourceSet = ResourceSet.Empty;

  private networkTracingInfo: NetworkTracingInfo = undefined;
  private isTopicWasteWater: boolean = true;
  private displayTextOPath: string = 'Bezeichnung';
  private displayCaption: string = 'Bezeichnung'; //fallback for localization

  private edgeDirectionDataSource: { caption: string; value: EdgeDirection }[];
  private stopSearchConditionDataSource: { caption: string; value: number }[];
  private filterModeDatasource: { caption: string; value: number }[];

  constructor(private resourceService: ResourceManagerService) {
    this.res$ = this.resourceService.getResourceSetAsObservable('basys-map-frame');
  }

  async ngOnInit(): Promise<void> {
    this.res = await this.res$.toPromise();
    await this.initDataSources();
  }

  private async initDataSources() {
    this.displayCaption = this.res.get('NetworkTracing-DisplayColumnCaption');
    this.edgeDirectionDataSource = [
      { caption: this.res.get('NetworkTracing-EdgeDirection-StartToEnd'), value: EdgeDirection.StartToEnd },
      { caption: this.res.get('NetworkTracing-EdgeDirection-EndToStart'), value: EdgeDirection.EndToStart },
      { caption: this.res.get('NetworkTracing-EdgeDirection-Both'), value: EdgeDirection.Both },
    ];

    this.stopSearchConditionDataSource = [
      { caption: this.res.get('NetworkTracing-SearchCondition-None'), value: 0 },
      { caption: this.res.get('NetworkTracing-SearchCondition-OnClosedObjects'), value: 1 },
      { caption: this.res.get('NetworkTracing-SearchCondition-OnClosableObjects'), value: 2 },
    ];

    this.filterModeDatasource = [
      { caption: this.res.get('NetworkTracing-FilterMode-None'), value: 0 },
      { caption: this.res.get('NetworkTracing-FilterMode-WorkArea'), value: 1 },
      { caption: this.res.get('NetworkTracing-FilterMode-DrainageArea'), value: 2 },
      { caption: this.res.get('NetworkTracing-FilterMode-Collector'), value: 3 },
    ];

    this.dropDownEdgeDirection.setupForArrayData(
      this.edgeDirectionDataSource,
      'value',
      'caption',
      [{ dataField: 'caption' }],
      this.edgeDirection
    );

    this.stopSearchDropDown.setupForArrayData(
      this.stopSearchConditionDataSource,
      'value',
      'caption',
      [{ dataField: 'caption' }],
      this.searchStopOption
    );

    this.filterModeDropDown.setupForArrayData(
      this.filterModeDatasource,
      'value',
      'caption',
      [{ dataField: 'caption' }],
      this.edgeFilterMode
    );

    const pipeQuery = this.createPipeQuery();
    await this.dropDownPipe.setupForQuery(
      pipeQuery,
      'Id',
      this.displayCaption,
      [{ dataField: this.displayCaption }],
      this.networkTracingInfo.InitialEntity.id
    );

    this.maxEdgeNumberBox.value = undefined;

    this.isTopicWasteWater = this.isWasteWater(this.networkTracingInfo.InitialObjectType.objectType);
    this.toggleControlsVisibilityByTopic(this.isTopicWasteWater);
  }

  private createPipeQuery(): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = this.networkTracingInfo.InitialObjectType.objectType.mainType.name;
    query.OPath = 'ParentId = $CurrentMunicipalityVariantId';

    const colums: QueryColumn[] = [];

    colums.push({ Name: 'Id', OPath: 'Id' });
    colums.push({ Name: this.displayCaption, OPath: this.displayTextOPath, SortOrder: QueryColumnSortOrder.Ascending });

    query.Columns = colums;
    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];

    return queryDocument;
  }

  private createArbeitsbereichQuery(): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = 'Arbeitsbereich';
    query.OPath = 'ParentId = $CurrentMunicipalityVariantId';

    const colums: QueryColumn[] = [];

    colums.push({ Name: 'Id', OPath: 'Id' });
    colums.push({ Name: this.displayCaption, OPath: this.displayTextOPath, SortOrder: QueryColumnSortOrder.Ascending });

    query.Columns = colums;
    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];

    return queryDocument;
  }

  private createAbwEntwaesserungsgebietQuery(): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = 'AbwEntwaesserungsgebiet';
    query.OPath = 'ParentId = $CurrentMunicipalityVariantId';

    const colums: QueryColumn[] = [];

    colums.push({ Name: 'Id', OPath: 'Id' });
    colums.push({
      Name: this.displayCaption,
      OPath: 'Entwaesserungsgebiet',
      SortOrder: QueryColumnSortOrder.Ascending,
    });

    query.Columns = colums;
    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];

    return queryDocument;
  }

  private createAbwSammlerQuery(): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = 'AbwSammler';
    query.OPath = 'ParentId = $CurrentMunicipalityVariantId';

    const colums: QueryColumn[] = [];

    colums.push({ Name: 'Id', OPath: 'Id' });
    colums.push({ Name: this.displayCaption, OPath: this.displayTextOPath, SortOrder: QueryColumnSortOrder.Ascending });

    query.Columns = colums;
    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];

    return queryDocument;
  }

  init(info: NetworkTracingInfo) {
    this.networkTracingInfo = info;
    this.selectedPipeId = info.InitialEntity.id;
    this.isPopupVisible = true;
    if (this.networkTracingPopup) {
      this.networkTracingPopup.visible = this.isPopupVisible;
    }
  }

  private toggleControlsVisibilityByTopic(isWasteWater: boolean) {
    this.filterModeDropDown.disabled = !isWasteWater;
    this.filterValueDropDown.disabled = !isWasteWater;
    this.connectionPipeCheck.disabled = !isWasteWater;
    this.onlyStartEndCheck.disabled = !isWasteWater;
  }

  public onPopupClosed(): void {
    this.isPopupVisible = false;
    this.onDialogCancelled.emit();
  }

  public onNetworkTracing(): void {
    const networkTraceModel: NetworkTraceModel = new NetworkTraceModel();
    networkTraceModel.EdgeObjectType = this.networkTracingInfo.InitialObjectType.objectType.mainType.name;
    networkTraceModel.StartEdgeIds = [this.selectedPipeId ?? this.networkTracingInfo.InitialEntity.id];
    networkTraceModel.EdgeDirection = this.edgeDirection;
    networkTraceModel.MaxSearchEdgeCount = this.maxEdgeCount ?? 0;
    networkTraceModel.StopSearchCondition = this.searchStopOption;
    networkTraceModel.StartEndEdgesOnly = this.onlyStartEndEdges;

    if (this.includeConnectionPipe) {
      networkTraceModel.EdgeObjectType = BasysMapFrameService.OBJECTTYPE_ABWLEITUNG;
    } else {
      networkTraceModel.EdgeObjectType = BasysMapFrameService.OBJECTTYPE_ABWHALTUNGBASE;
    }

    if (this.edgeFilterMode != EdgeFilterModeEnum.None && this.edgeFilterValue) {
      networkTraceModel.EdgeFilterMode = this.edgeFilterMode;
      networkTraceModel.EdgeFilterExpression = this.edgeFilterValue;
    }

    this.networkTracingInfo.TracingModel = networkTraceModel;
    this.onDialogClosed.emit(this.networkTracingInfo);
  }

  async onFilterModeChanged(event: any) {
    await this.refreshFilterValue();
  }

  private async refreshFilterValue() {
    this.edgeFilterValue = undefined;
    await this.refreshFilterValueDataSource();
  }

  private async refreshFilterValueDataSource() {
    if (this.edgeFilterMode === EdgeFilterModeEnum.None) {
      this.filterValueDropDown.dataSource = [];
      this.edgeFilterValue = null;
    } else {
      let query: QueryObjectsModel = undefined;
      if (this.edgeFilterMode === EdgeFilterModeEnum.Arbeitsbereich) {
        query = this.createArbeitsbereichQuery();
      } else if (this.edgeFilterMode === EdgeFilterModeEnum.AbwEntwaesserungsgebiet) {
        query = this.createAbwEntwaesserungsgebietQuery();
      } else if (this.edgeFilterMode === EdgeFilterModeEnum.AbwSammler) {
        query = this.createAbwSammlerQuery();
      }

      if (query) {
        await this.filterValueDropDown.setupForQuery(
          query,
          'Id',
          this.displayCaption,
          [{ dataField: this.displayCaption }],
          this.edgeFilterValue
        );
      }
    }
  }

  private isWasteWater(objectType: ObjectTypeInfoViewModel) {
    let retWW = false;
    if (objectType && objectType.mainType.name === BasysMapFrameService.OBJECTTYPE_ABWLEITUNG) {
      retWW = true;
    }

    return retWW;
  }
}
